import { OrderStatusBadge } from "@/components/ui/status-badge";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";
import { ArrowUpRight, ArrowDownRight, Truck, User, Container } from "lucide-react";
import { DataCard } from "@/components/ui/data-card";
import { Order, Customer, Tractor, Driver, Location } from "@/types/tms";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatWindowCompact } from "@/lib/order-time";
import { resolveOrderEndpoints } from "@/lib/order-endpoints";

type OrderWithRelations = Order & {
  billing_party?: Customer | null;
  assigned_tractor?: Tractor | null;
  assigned_operator?: Driver | null;
};

interface MultiLegRouteChainProps {
  parentOrder: OrderWithRelations;
  legs: OrderWithRelations[];
  onSelectLeg?: (legId: string) => void;
}

export function MultiLegRouteChain({ parentOrder, legs, onSelectLeg }: MultiLegRouteChainProps) {
  const viewerTz = useViewerTimezone();
  const { data: locations = [] } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const { data } = await supabase.from("locations").select("*").order("short_code");
      return (data ?? []) as Location[];
    },
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="space-y-3">
      {legs.map((leg) => {
        const { from, to } = resolveOrderEndpoints(leg, locations);
        const fromLabel = from.label;
        const toLabel = to.label;

        return (
          <DataCard key={leg.id} onClick={() => onSelectLeg?.(leg.id)}>
            <DataCard.Header>
              <span className="flex items-baseline gap-2 min-w-0">
                <span className="font-bold text-lg leading-tight text-foreground truncate py-0.5">
                  Leg {leg.leg_sequence}
                </span>
                <span className="font-mono text-sm text-muted-foreground truncate">
                  {parentOrder.unique_id}
                </span>
              </span>
            </DataCard.Header>

            <DataCard.Chips>
              <OrderStatusBadge status={leg.status} />
            </DataCard.Chips>

            <DataCard.Body>
              <DataCard.Field
                icon={ArrowUpRight}
                secondary={formatWindowCompact(
                  leg.ready_time,
                  (leg as any).ready_time_end,
                  viewerTz,
                  "MMM d",
                  "HH:mm",
                  { withTz: true },
                )}
              >
                {fromLabel}
              </DataCard.Field>
              <DataCard.Field
                icon={ArrowDownRight}
                secondary={formatWindowCompact(
                  leg.requested_delivery_time,
                  (leg as any).requested_delivery_time_end,
                  viewerTz,
                  "MMM d",
                  "HH:mm",
                  { withTz: true },
                )}
              >
                {toLabel}
              </DataCard.Field>
            </DataCard.Body>

            <div className="flex flex-col gap-2">
              <DataCard.Tile
                icon={Truck}
                label="Truck ID"
                value={
                  leg.assigned_tractor?.name ?? (
                    <span className="text-muted-foreground font-normal">No truck assigned</span>
                  )
                }
              />
              <DataCard.Tile
                icon={Container}
                label="Trailer ID"
                value={
                  leg.trailer_id ?? (
                    <span className="text-muted-foreground font-normal">No trailer assigned</span>
                  )
                }
              />
              <DataCard.Tile
                icon={User}
                label="Operator name(s)"
                value={
                  [leg.assigned_operator?.name, (leg as any).assigned_operator_2?.name]
                    .filter(Boolean)
                    .join(", ") || (
                    <span className="text-muted-foreground font-normal">No operators assigned</span>
                  )
                }
              />
            </div>
          </DataCard>
        );
      })}

      {legs.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">No child legs found</p>
      )}
    </div>
  );
}
